import DoctorCardView from "@/components/card/DoctorCardView";
import CreateDoctorAccount from "@/components/drawer/CreateDoctorAccount";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { DataTable, DataTableOptions, DataTableRef } from "@/components/tables";
import { DataGrid, DataGridOptions, DataGridRef } from "@/components/grids";
import { useAccessCheck } from "@/hooks/useAccess";
import { DoctorOverview } from "@/types";
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuGroup,
  MenuDivider,
  Tab,
  TabList,
  Tabs,
  VStack,
} from "@chakra-ui/react";
import { Filter, LayoutGrid, List, Plus, Search } from "lucide-react";
import React, { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { mapToDoctorOverview } from "@/lib/utils/doctorHelper";
import apiClient from "@/lib/api/client";

const DoctorsPage = () => {
  const navigate = useNavigate();
  const access = useAccessCheck();

  const searchRef = useRef<HTMLInputElement>(null);
  const drawerRef = useRef<HTMLButtonElement>(null);
  const tableRef = useRef<DataTableRef>(null);
  const gridRef = useRef<DataGridRef>(null);

  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [isGrid, setIsGrid] = useState(0);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<DoctorOverview | null>(null);
  const [roles, setRoles] = useState<any[]>([]);

  // Filter state
  const [availabilityFilter, setAvailabilityFilter] = useState<string | null>(null);
  const [genderFilter, setGenderFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  const activeFilterCount = [availabilityFilter, genderFilter, statusFilter].filter(Boolean).length;

  const handleClearFilters = () => {
    setAvailabilityFilter(null);
    setGenderFilter(null);
    setStatusFilter(null);
  };

  const onDrawerClose = () => {
    setIsDrawerOpen(false);
  };

  const handleSearch = () => {
    const query = searchRef.current?.value || "";
    setSearchQuery(query);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleSearch();
  };

  const permissions = useMemo(
    () => ({
      can_edit: access.can_edit,
      can_delete: access.can_delete,
      can_create: access.can_create,
      can_view: access.can_view,
    }),
    [access]
  );

  const tableParams = useMemo(
    () => ({
      search: searchQuery,
      ...(availabilityFilter && { availability: availabilityFilter }),
      ...(genderFilter && { gender: genderFilter }),
      ...(statusFilter && { status: statusFilter }),
    }),
    [searchQuery, availabilityFilter, genderFilter, statusFilter]
  );

  const tableOptions: DataTableOptions = useMemo(
    () => ({
      dataFields: {
        user_fullname: {
          name: "Doctor Name",
          maxWidth: "250px",
        },
        department: {
          name: "Department",
          maxWidth: "200px",
        },
        designation: {
          name: "Designation",
          maxWidth: "300px",
        },
        next_available_date: {
          name: "Available Date",
          render: (value: string) => {
            if (!value) return "N/A";
            try {
              const date = parseISO(value);
              return format(date, "EEE, dd MMM yyyy");
            } catch (error) {
              return "N/A";
            }
          },
        },
      },
      dataSource: "/user-management/users/doctors",
      primaryKey: "id",
      softDelete: true,
      displayInModal: true,
      noRecordText: "No Doctors found",
      include: "image,department,availabilities,designation,branch",
    }),
    []
  );

  const handleEditUser = async (user: DoctorOverview) => {
    try {
      const response = await apiClient.get(`/user-management/users/doctor/${user.id}`);
      const userData = response.data?.data || response.data;

      let roleId = "";
      if (userData.user_role) {
        if (typeof userData.user_role === "string") {
          try {
            roleId = JSON.parse(userData.user_role).id?.toString() || "";
          } catch {
            roleId = roles.find((r) => r.name === userData.user_role)?.id.toString() || "";
          }
        } else if (userData.user_role.id) {
          roleId = userData.user_role.id.toString();
        }
      } else if (userData.role_id) {
        roleId = userData.role_id.toString();
      }

      const userToEdit: DoctorOverview = {
        ...userData,
        user_role: roleId,
      };
      setEditingUser(userToEdit);
      setIsDrawerOpen(true);
    } catch (error: any) {
      console.error(error);
    }
  };

  const gridOptions: DataGridOptions = useMemo(
    () => ({
      dataSource: "/user-management/users/doctors",
      primaryKey: "id",
      itemsPerPage: 15,
      noRecordText: "No Doctors found",
      include: "image,department,availabilities,designation,branch",
      renderCard: (doctor: any) => (
        <DoctorCardView key={doctor.id} doc={mapToDoctorOverview(doctor)} />
      ),
    }),
    []
  );

  const refreshDoctors = () => {
    if (isGrid) {
      gridRef.current?.reload();
    } else {
      tableRef.current?.reload();
    }
  };

  return (
    <AdminLayout>
      <Box
        minH="calc(100vh - 110px)"
        overflow="hidden"
        display="flex"
        flexDirection="column"
        bg="#FEFCF8"
        py={1}
        px={{ base: 3, md: 5 }}
      >
        <VStack spacing={2} align="stretch" flex="1" overflow="hidden" minH={0} h="32px">
          <Flex shrink={0} direction="column" flex="1" minH={0} overflow="hidden">
            {/* Header Section */}
            <Box flexShrink={0}>
              <Flex
                direction={{ base: "column", lg: "row" }}
                justify="space-between"
                align={{ base: "flex-start", lg: "center" }}
                gap={{ base: 3, lg: 0 }}
              >
                <Box>
                  <Badge
                    className="rounded-sm border-[#2E37A4] border"
                    py="4px"
                    px="2px"
                    fontSize={{ base: "2xs", lg: "xs" }}
                    colorScheme="purple"
                  >
                    TOTAL DOCTORS: {total}
                  </Badge>
                </Box>
                <Flex py={1} pt={2} pb={2} shrink={0} justify="space-between" direction={{ base: "column", lg: "row" }}>
                  <HStack
                    spacing={3}
                    flexWrap="wrap"
                    h="32px"
                    w="100%"
                    justify={{ base: "space-between", sm: "flex-start" }}
                  >
                    {/* Search */}
                    <Box
                      flex="0 1 auto"
                      minW={{ base: "120px", sm: "150px", md: "180px" }}
                      maxW={{ base: "180px", md: "200px" }}
                      h="32px"
                      bg="white"
                      order={{ base: 1, sm: 1 }}
                    >
                      <InputGroup size="lg" h="full">
                        <InputLeftElement pointerEvents="none" h="full">
                          <Search size={16} color="gray" />
                        </InputLeftElement>
                        <Input
                          ref={searchRef}
                          placeholder="Search doctors..."
                          defaultValue={searchQuery}
                          onChange={handleSearch}
                          onKeyDown={handleKeyDown}
                          fontSize={{ base: "sm", lg: "md" }}
                          h="full"
                        />
                      </InputGroup>
                    </Box>

                    {/* Filter Button */}
                    <Menu closeOnSelect={false}>
                      <MenuButton
                        as={Button}
                        variant="outline"
                        size="sm"
                        h="32px"
                        bg="white"
                        fontSize={{ base: "xs", lg: "sm" }}
                        leftIcon={<Filter size={16} />}
                        order={{ base: 2, sm: 2 }}
                        isDisabled={!permissions.can_view}
                      >
                        Filter {activeFilterCount > 0 ? `(${activeFilterCount})` : ""}
                      </MenuButton>
                      <MenuList minW="200px" zIndex={1400}>

                        {/* Availability */}
                        <MenuGroup title="Availability" fontSize="xs" fontWeight="semibold" color="gray.500" mx={3} mt={1}>
                          <MenuItem
                            fontSize="sm"
                            fontWeight={!availabilityFilter ? "semibold" : "normal"}
                            color={!availabilityFilter ? "blue.600" : "inherit"}
                            onClick={() => setAvailabilityFilter(null)}
                          >
                            All
                          </MenuItem>
                          <MenuItem
                            fontSize="sm"
                            fontWeight={availabilityFilter === "available" ? "semibold" : "normal"}
                            color={availabilityFilter === "available" ? "blue.600" : "inherit"}
                            onClick={() => setAvailabilityFilter("available")}
                          >
                            Available
                          </MenuItem>
                          <MenuItem
                            fontSize="sm"
                            fontWeight={availabilityFilter === "unavailable" ? "semibold" : "normal"}
                            color={availabilityFilter === "unavailable" ? "blue.600" : "inherit"}
                            onClick={() => setAvailabilityFilter("unavailable")}
                          >
                            Unavailable
                          </MenuItem>
                        </MenuGroup>

                        <MenuDivider />

                        {/* Gender */}
                        <MenuGroup title="Gender" fontSize="xs" fontWeight="semibold" color="gray.500" mx={3}>
                          <MenuItem
                            fontSize="sm"
                            fontWeight={!genderFilter ? "semibold" : "normal"}
                            color={!genderFilter ? "blue.600" : "inherit"}
                            onClick={() => setGenderFilter(null)}
                          >
                            All
                          </MenuItem>
                          <MenuItem
                            fontSize="sm"
                            fontWeight={genderFilter === "Male" ? "semibold" : "normal"}
                            color={genderFilter === "Male" ? "blue.600" : "inherit"}
                            onClick={() => setGenderFilter("Male")}
                          >
                            Male
                          </MenuItem>
                          <MenuItem
                            fontSize="sm"
                            fontWeight={genderFilter === "Female" ? "semibold" : "normal"}
                            color={genderFilter === "Female" ? "blue.600" : "inherit"}
                            onClick={() => setGenderFilter("Female")}
                          >
                            Female
                          </MenuItem>
                        </MenuGroup>

                        <MenuDivider />

                        {/* Status */}
                        <MenuGroup title="Status" fontSize="xs" fontWeight="semibold" color="gray.500" mx={3}>
                          <MenuItem
                            fontSize="sm"
                            fontWeight={!statusFilter ? "semibold" : "normal"}
                            color={!statusFilter ? "blue.600" : "inherit"}
                            onClick={() => setStatusFilter(null)}
                          >
                            All
                          </MenuItem>
                          <MenuItem
                            fontSize="sm"
                            fontWeight={statusFilter === "1" ? "semibold" : "normal"}
                            color={statusFilter === "1" ? "blue.600" : "inherit"}
                            onClick={() => setStatusFilter("1")}
                          >
                            Active
                          </MenuItem>
                          <MenuItem
                            fontSize="sm"
                            fontWeight={statusFilter === "0" ? "semibold" : "normal"}
                            color={statusFilter === "0" ? "blue.600" : "inherit"}
                            onClick={() => setStatusFilter("0")}
                          >
                            Inactive
                          </MenuItem>
                        </MenuGroup>

                        {/* Clear Filters */}
                        {activeFilterCount > 0 && (
                          <>
                            <MenuDivider />
                            <MenuItem fontSize="sm" color="red.500" onClick={handleClearFilters}>
                              Clear All Filters
                            </MenuItem>
                          </>
                        )}
                      </MenuList>
                    </Menu>

                    {/* List / Grid Toggle */}
                    <Tabs variant="unstyled" h="32px" order={{ base: 3, sm: 3 }}>
                      <TabList className="rounded-md border flex items-center" h="32px">
                        <Tab
                          className="w-8 h-8"
                          onClick={() => setIsGrid(0)}
                          p={0}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <Box className={`p-1 rounded-sm ${!isGrid ? "bg-gray-400/20" : "bg-white"}`}>
                            <List size={16} />
                          </Box>
                        </Tab>
                        <Tab
                          className="w-8 h-8"
                          onClick={() => setIsGrid(1)}
                          p={0}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <Box className={`p-1 rounded-sm text-black ${isGrid ? "bg-gray-400/20" : "bg-white"}`}>
                            <LayoutGrid size={16} />
                          </Box>
                        </Tab>
                      </TabList>
                    </Tabs>

                    {permissions.can_create && (
                      <>
                        <IconButton
                          aria-label="New Doctor"
                          icon={<Plus size={16} />}
                          bg="red.500"
                          _hover={{ bg: "#bf3c1fff" }}
                          color="white"
                          size="sm"
                          h="32px"
                          w="32px"
                          onClick={() => setIsDrawerOpen(true)}
                          display={{ base: "flex", sm: "none" }}
                          order={{ base: 4, sm: 5 }}
                          minW="32px"
                        />
                        <Button
                          py="6px"
                          px="10px"
                          bg="red.500"
                          _hover={{ bg: "#bf3c1fff" }}
                          colorScheme="blue"
                          fontSize={{ base: "xs", lg: "sm" }}
                          leftIcon={<Plus size={16} />}
                          h="32px"
                          onClick={() => setIsDrawerOpen(true)}
                          display={{ base: "none", sm: "flex" }}
                          order={{ base: 5, sm: 5 }}
                          flexShrink={0}
                          whiteSpace="nowrap"
                        >
                          New Doctor
                        </Button>
                      </>
                    )}
                  </HStack>
                </Flex>
              </Flex>
            </Box>

            {/* Content Section */}
            {isGrid ? (
              <DataGrid
                ref={gridRef}
                options={gridOptions}
                params={tableParams}
                setTotal={setTotal}
                gridColumns={{
                  base: "repeat(1, 1fr)",
                  sm: "repeat(1, 1fr)",
                  md: "repeat(2, 1fr)",
                  lg: "repeat(3, 1fr)",
                  xl: "repeat(4, 1fr)",
                  "2xl": "repeat(3, 1fr)",
                }}
              />
            ) : (
              <DataTable
                ref={tableRef}
                options={tableOptions}
                params={tableParams}
                access={permissions}
                setTotal={setTotal}
                showCheckbox={false}
                onOpenModal={handleEditUser}
                onOpenItem={(id) => navigate(`/profile/${id}`)}
              />
            )}
          </Flex>
        </VStack>
      </Box>
      <CreateDoctorAccount
        isOpen={isDrawerOpen}
        onClose={onDrawerClose}
        btnRef={drawerRef}
        refreshDoctors={refreshDoctors}
        editingUser={editingUser}
      />
    </AdminLayout>
  );
};

export default DoctorsPage;